import { GetStaticProps, GetStaticPaths } from 'next'

import Link from '@/atoms/Link/Link'
import Layout from '@/templates/Layout/Layout'
import TextSampleDetails from '@/templates/SampleDetails/TextSampleDetails'
import DesignSampleDetails from '@/templates/SampleDetails/DesignSampleDetails'
import GettingStarted from '@/templates/GettingStarted/GettingStarted'

/* utils */
import { getWordpressGraphqlData } from '@/utils/api/wrapper'
import { getPostExcerpt } from '@/utils/functions/blog'
import {
  constructBlogPostSchema,
  constructBreadcrumbJsonSchema
} from '@/utils/functions/jsonSchema'
import {
  sampleDetailsPage,
  sampleListQuery
} from '@/utils/queries/samples_list'
import { getContentCategoryMetadata } from '@/utils/functions/samples'
import {
  INSTANT_REVALIDATION,
  TEN_MINS_REVALIDATION
} from '@/utils/constants/revalidation'
import { triggerServiceOrderingFlow } from '@/utils/functions/order'
import { capture404PageError } from '@/utils/functions/elk-server'

interface Props {
  post: SamplePostDetails
  postContentCategory: WpContentCategoryName
  similarPosts: SamplePost[]
}

const SampleDetailsPage = ({
  post,
  postContentCategory,
  similarPosts
}: Props) => {
  const url = `https://www.peppercontent.io/samples/${post.slug}`
  const featuredImage =
    post.featuredImage?.node.sourceUrl ??
    post.sampleDesignPostMetadata?.image.sourceUrl ??
    undefined
  const excerpt = getPostExcerpt(post.excerpt)

  const contentCategoryMetadata =
    getContentCategoryMetadata(postContentCategory)

  const breadcrumbs = [
    { name: 'Samples', url: '/samples/' },
    {
      name: contentCategoryMetadata.title,
      url: contentCategoryMetadata.slug
    },
    { name: post.title, url: `/samples/${post.slug}/` }
  ]

  const jsonSchema = [
    constructBreadcrumbJsonSchema(breadcrumbs),
    constructBlogPostSchema({
      url,
      title: post.title,
      description: excerpt,
      authorName: post.author.node.name,
      featuredImage: featuredImage ?? '',
      datePublished: new Date(post.date).toISOString().slice(0, 10),
      dateModified: post.modified.slice(0, 10)
    })
  ]

  const metadata = [
    { property: 'og:title', content: post.title },
    { property: 'og:description', content: excerpt },
    { property: 'description', content: excerpt },
    { property: 'og:image', content: featuredImage ?? '' },
    { property: 'og:type', content: 'blog' },
    { property: 'og:url', content: url }
  ]

  const onCtaClick = () => {
    triggerServiceOrderingFlow({})
  }

  return (
    <Layout
      title={`${post.title} | Pepper Content`}
      canonical={`/samples/${post.slug}/`}
      jsonSchema={jsonSchema}
      meta={metadata}
      noIndex={true}
    >
      <section className="bg-yellow-10  |  px-5 lg:px-18 py-3 mb-12">
        <ul className="flex">
          {breadcrumbs.map((item, index) => {
            const isLastItem = index === breadcrumbs.length - 1
            const showSeparator = index < breadcrumbs.length - 2
            if (isLastItem) return null
            return (
              <li key={item.url}>
                <Link
                  href={item.url}
                  className="focus:outline-none focus:underline"
                >
                  {item.name}
                </Link>
                {showSeparator && <span>&nbsp;/&nbsp;</span>}
              </li>
            )
          })}
        </ul>
      </section>

      {(postContentCategory === 'Content Category: Text' ||
        postContentCategory === 'Content Category: Language & Translation') && (
        <TextSampleDetails
          post={post}
          similarPosts={similarPosts}
          contentCategory={postContentCategory}
        />
      )}

      {postContentCategory === 'Content Category: Design' && (
        <DesignSampleDetails
          post={post}
          similarPosts={similarPosts}
          contentCategory={postContentCategory}
        />
      )}

      <GettingStarted
        title="Get a custom estimate"
        list={[
          { id: '1', Item: 'Quality guaranteed' },
          { id: '2', Item: 'Scales with you' },
          { id: '3', Item: 'Easy to get started' }
        ]}
        ctaTitle="Get Started"
        sectionName="Sample_Details_Page"
        onCtaClick={onCtaClick}
      />
    </Layout>
  )
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = params?.slug as string
  let similarPosts: SamplePost[] = []

  const result = await getWordpressGraphqlData<{
    post: SamplePostDetails | null
  }>(sampleDetailsPage(slug))

  const contentCategory = result.post?.categories.edges.find(category =>
    category.node.name.startsWith('Content Category: ')
  )

  // if post or content category is not present, redirect to 404 page
  if (result.post === null || contentCategory === undefined) {
    capture404PageError(`/samples/${slug}/`)
    return { notFound: true, revalidate: INSTANT_REVALIDATION }
  }

  // fetch posts with same content category
  const similarPostsResult = await getWordpressGraphqlData<{
    posts: { nodes: SamplePost[] }
  }>(
    sampleListQuery({ categories: [contentCategory.node.name], limit: 9 }).loc
      ?.source.body
  )
  similarPosts = similarPostsResult.posts.nodes

  return {
    props: {
      post: result.post,
      postContentCategory: contentCategory?.node.name,
      similarPosts
    },
    revalidate: TEN_MINS_REVALIDATION
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  }
}

export default SampleDetailsPage
